/**
 * useActivation.js — Hook that fires the activation endpoint after a
 * successful integration connection and manages the "Your AI just improved"
 * + "You can now run these systems" state.
 *
 * Usage:
 *   const { triggerActivation, activationData, dismiss } = useActivation();
 *   // After a connection succeeds:
 *   triggerActivation('gmail');
 */
import { useState, useCallback, useRef } from 'react';
import { BACKEND_URL } from '../config';

const DISMISS_KEY = 'activation_dismissed';

function getDismissed() {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]');
  } catch { return []; }
}

export default function useActivation() {
  const [activationData, setActivationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inflightRef = useRef(null);

  const triggerActivation = useCallback(async (providerSlug) => {
    if (!providerSlug) return null;

    // Prevent duplicate concurrent calls for same provider
    if (inflightRef.current === providerSlug) return null;
    inflightRef.current = providerSlug;

    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/integrations/${providerSlug}/activated`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) return null;

      const data = await res.json();

      // Don't surface empty/noisy results
      if (!data.improvements || data.improvements.length === 0) {
        return data;
      }

      // Don't re-show if user already dismissed this event
      const dismissed = getDismissed();
      if (dismissed.includes(data.event_id)) {
        return data;
      }

      setActivationData(data);
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
      inflightRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    if (activationData?.event_id) {
      const dismissed = getDismissed();
      dismissed.push(activationData.event_id);
      // Keep only last 20 to avoid storage bloat
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed.slice(-20)));
    }
    setActivationData(null);
  }, [activationData]);

  return { triggerActivation, activationData, dismiss, loading };
}
